import React, { useEffect, useRef, useState } from 'react';
import { Box, ButtonBase, CircularProgress, IconButton, InputBase, Typography } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import MedicationOutlinedIcon from '@mui/icons-material/MedicationOutlined';
import { LockOutlinedIcon } from '../../icons';
import { color, r, radius } from '../../theme/tokens';
import useMedicationSearch, { resolveMedicationPick } from '../../hooks/useMedicationSearch';
import { GUEST_INTERACTION_MAX_MEDICATIONS } from '../../utils/guestMedications';

// The medications panel (STACK-563 mock, states s2–s7): lists what is added,
// each removable, and searches to add more. The same content renders in a
// desktop popover and a phone bottom sheet (GuestMedicationsControl).
//
// Search goes through the POST lookup, so what a visitor types never sits in
// a URL (docs/guest-medication-check.md §3.1). It waits for 2 characters and
// a 300 ms pause, as the profile's picker does — `useMedicationSearch`, which
// the interaction checker page shares (STACK-587). There is no free-text
// entry: the check needs an RxNorm ingredient, and a typed name could not be
// checked.

const pillIcon = <MedicationOutlinedIcon sx={{ fontSize: r(16), color: color.evergreen }} aria-hidden />;

// "Generic · 2.5, 5, 10 mg tablet", or "Brand of lisinopril · …".
const resultSubline = (result) => {
  const kind = result.is_brand && result.generic_name
    ? `Brand of ${result.generic_name.toLowerCase()}`
    : 'Generic';
  const strengths = Array.isArray(result.strengths) ? result.strengths.slice(0, 4).join(', ') : '';
  return strengths ? `${kind} · ${strengths}` : kind;
};

const sameName = (a, b) => Boolean(a && b) && a.trim().toLowerCase() === b.trim().toLowerCase();

// A result already added: matched by name, since the ingredient code is only
// known once a result is picked (the details lookup resolves it).
const isAlreadyAdded = (result, added) => added.some((m) =>
  sameName(result.display_name, m.display_name)
  || sameName(result.generic_name, m.ingredient_name)
  || sameName(result.display_name, m.ingredient_name));

const Highlight = ({ text, term }) => {
  const i = term ? text.toLowerCase().indexOf(term.toLowerCase()) : -1;
  if (i < 0) return text;
  return (
    <>
      {text.slice(0, i)}
      <Box component="span" sx={{ fontWeight: 800 }}>{text.slice(i, i + term.length)}</Box>
      {text.slice(i + term.length)}
    </>
  );
};

const sectionLabelSx = {
  fontSize: r(11),
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: color.inkSoft,
  px: r(16),
  pb: r(6),
  m: 0,
};

const emptyStateSx = {
  px: r(16),
  pt: r(16),
  pb: r(8),
  fontSize: r(14),
  lineHeight: 1.5,
  color: color.textBody,
};

export default function MedicationsPanel({
  medications,
  onAdd,
  onRemove,
  onClose,
  sheet = false,
}) {
  const [term, setTerm] = useState('');
  const [highlight, setHighlight] = useState(0);
  const [adding, setAdding] = useState(null);
  const [addError, setAddError] = useState(null);
  const inputRef = useRef(null);
  // view: idle | loading | results | none | error
  const { query, results, view, retry } = useMedicationSearch(term);

  const count = medications.length;
  const atCap = count >= GUEST_INTERACTION_MAX_MEDICATIONS;

  // A new answer starts the keyboard highlight at its first result.
  useEffect(() => { setHighlight(0); }, [results]);

  const pick = async (result) => {
    if (!result || adding || isAlreadyAdded(result, medications)) return;
    const rxcui = result.rxcuis?.[0];
    setAddError(null);
    if (!rxcui) {
      setAddError(result.display_name);
      return;
    }
    setAdding(result.display_name);
    try {
      const resolved = await resolveMedicationPick(result);
      const outcome = resolved ? onAdd(resolved) : 'invalid';
      if (outcome === 'added') {
        setTerm('');
        inputRef.current?.focus();
      } else {
        // duplicate / full / invalid: keep the query; do not pretend it added.
        setAddError(result.display_name);
      }
    } catch (_) {
      setAddError(result.display_name);
    } finally {
      setAdding(null);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
      return;
    }
    if (view !== 'results' || !results.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => (h + 1) % results.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => (h - 1 + results.length) % results.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      pick(results[highlight]);
    }
  };

  const body = () => {
    if (view === 'loading') {
      return (
        <Box sx={{ ...emptyStateSx, display: 'flex', alignItems: 'center', gap: r(8), color: color.inkSoft }}>
          <CircularProgress size={r(14)} sx={{ color: color.inkSoft }} />
          Searching…
        </Box>
      );
    }
    if (view === 'results') {
      return (
        <Box
          component="ul"
          role="listbox"
          aria-label="Matching medications"
          sx={{ listStyle: 'none', m: 0, mt: { xs: r(8), sm: r(4) }, px: r(8) }}
        >
          {results.map((res, i) => {
            const done = isAlreadyAdded(res, medications);
            return (
              <Box
                component="li"
                key={`${res.display_name}-${i}`}
                role="option"
                aria-selected={i === highlight}
                aria-disabled={done || undefined}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => pick(res)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: r(12),
                  // Tighter on desktop (round 7) so the panel needs less
                  // room; phones keep their 44px-plus targets.
                  px: r(8),
                  py: { xs: r(8), sm: r(5) },
                  minHeight: { xs: r(52), sm: r(44) },
                  borderRadius: radius.sm,
                  cursor: done ? 'default' : 'pointer',
                  backgroundColor: i === highlight ? color.mint : 'transparent',
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: { xs: r(15), sm: r(14) }, fontWeight: 600, color: color.ink, lineHeight: 1.35 }}>
                    <Highlight text={res.display_name} term={query} />
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: r(12),
                      color: color.inkSoft,
                      mt: r(2),
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {resultSubline(res)}
                  </Typography>
                </Box>
                <Typography
                  component="span"
                  sx={{ fontSize: r(12), fontWeight: 700, flex: 'none', color: done ? color.inkMuted : color.linkHover }}
                >
                  {adding === res.display_name ? 'Adding…' : done ? 'Added' : 'Add'}
                </Typography>
              </Box>
            );
          })}
        </Box>
      );
    }
    if (view === 'none') {
      return (
        <Box sx={emptyStateSx} role="status">
          <Box component="b" sx={{ display: 'block', color: color.ink, pb: r(4) }}>
            No medications match "{query}".
          </Box>
          Check the spelling, or try the generic name printed on the label under the brand name.
        </Box>
      );
    }
    if (view === 'error') {
      return (
        <Box sx={emptyStateSx} role="alert">
          <Box component="b" sx={{ display: 'block', color: color.ink, pb: r(4) }}>
            We can't search medications right now.
          </Box>
          Try again in a minute. You can still create your stack without them, but they won't be checked.
          <Box>
            <ButtonBase
              onClick={retry}
              sx={{
                mt: r(12),
                minHeight: r(44),
                px: r(16),
                borderRadius: r(10),
                border: `${r(1)} solid ${color.line}`,
                backgroundColor: color.paper,
                color: color.evergreen,
                fontWeight: 600,
                fontSize: r(14),
              }}
            >
              Try again
            </ButtonBase>
          </Box>
        </Box>
      );
    }
    return (
      <Box sx={{ ...emptyStateSx, color: color.inkSoft }}>
        Type at least 2 letters. Brand or generic names both work.
      </Box>
    );
  };

  return (
    <Box
      role="dialog"
      aria-label="Your medications"
      data-testid="guest-medications-panel"
      sx={{
        backgroundColor: color.paper,
        color: color.ink,
        textAlign: 'left',
        width: '100%',
        // Fills the popover or sheet; the middle scrolls, the rest stays put.
        flex: '1 1 auto',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {sheet && (
        // Centred by its parent: App.css resets every MuiBox's side and
        // bottom margins, so `mx: 'auto'` (and any mx/mb here) does nothing.
        <Box aria-hidden sx={{ display: 'flex', justifyContent: 'center', pt: r(8) }}>
          <Box sx={{ width: r(36), height: r(4), borderRadius: r(2), backgroundColor: color.line }} />
        </Box>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: r(8), pt: { xs: r(12), sm: r(8) }, pr: r(12), pb: { xs: r(8), sm: r(6) }, pl: r(16) }}>
        <Typography component="h2" sx={{ m: 0, fontSize: r(16), fontWeight: 700, flex: 1 }}>
          Your medications
        </Typography>
        {/* A quiet count, not "n of 5", which read like steps (Aravind,
            2026-10-09). Nothing at 0, and at the cap the message below says it. */}
        {count > 0 && !atCap && (
          <Typography component="span" sx={{ fontSize: r(12), color: color.inkSoft, fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
            {count} added
          </Typography>
        )}
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ width: { xs: r(44), sm: r(32) }, height: { xs: r(44), sm: r(32) }, color: color.inkSoft, borderRadius: radius.sm }}
        >
          <CloseRoundedIcon sx={{ fontSize: r(20) }} />
        </IconButton>
      </Box>
      {/* No description line under the title (Aravind, 2026-10-09, round
          5): the fixed parts are trimmed so the list keeps its room. */}

      {/* Pinned under the header: the search, or at the cap the limit
          message (Aravind, 2026-10-09). */}
      <Box sx={{ flex: 'none', pb: r(4) }}>
      {atCap ? (
        <Box sx={{ px: r(16) }}>
        <Box
          role="status"
          sx={{ p: r(12), borderRadius: radius.md, backgroundColor: color.surface, fontSize: r(14), lineHeight: 1.5, color: color.textBody }}
        >
          {`That's ${GUEST_INTERACTION_MAX_MEDICATIONS}, the most for a free check. Members can check all their medications.`}
        </Box>
        </Box>
      ) : (
          <Box sx={{ px: r(16) }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: r(8),
              border: `${r(1)} solid ${color.accent}`,
              borderRadius: r(10),
              px: r(12),
              minHeight: { xs: r(44), sm: r(40) },
              boxShadow: `0 0 0 ${r(3)} rgba(10,156,95,0.12)`,
            }}
          >
            <SearchRoundedIcon sx={{ fontSize: r(18), color: color.inkSoft, flex: 'none' }} aria-hidden />
            <InputBase
              inputRef={inputRef}
              autoFocus
              value={term}
              onChange={(e) => { setTerm(e.target.value); setAddError(null); }}
              onKeyDown={onKeyDown}
              placeholder="Search medications, e.g. metformin"
              inputProps={{
                'aria-label': 'Search medications',
                autoComplete: 'off',
                autoCorrect: 'off',
                spellCheck: 'false',
              }}
              // 16px floor: iOS zooms the page when focusing a smaller input.
              sx={{ flex: 1, minWidth: 0, fontSize: '1rem', color: color.ink }}
            />
          </Box>
          </Box>
      )}
      </Box>

      {/* The one scrolling region: results, then what is added. */}
      <Box sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
        {!atCap && (
          <>
          {addError && (
            <Typography role="alert" sx={{ ...emptyStateSx, pt: r(8), pb: 0, fontSize: r(13) }}>
              We couldn't add {addError}. Try again, or try its generic name.
            </Typography>
          )}
            {body()}
          </>
        )}
      {count > 0 && (
        <>
          <Typography component="p" sx={{ ...sectionLabelSx, pt: r(12) }}>Added</Typography>
          <Box component="ul" sx={{ listStyle: 'none', m: 0, pb: r(12), px: r(16) }}>
            {medications.map((m) => (
              <Box
                component="li"
                key={m.in_rxcui}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: r(10),
                  py: r(4),
                  pl: r(12),
                  border: `${r(1)} solid ${color.line}`,
                  borderRadius: radius.md,
                  backgroundColor: color.surface,
                  '& + &': { mt: r(6) },
                }}
              >
                {pillIcon}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: r(15), fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {m.display_name}
                  </Typography>
                  {/* A brand shows its generic underneath (s6: "Zestril",
                      "Brand of lisinopril"); a generic name already says it. */}
                  {m.ingredient_name
                    && !m.display_name.toLowerCase().includes(m.ingredient_name.toLowerCase()) && (
                    <Typography sx={{ fontSize: r(12), color: color.inkSoft }}>
                      {`Brand of ${m.ingredient_name.toLowerCase()}`}
                    </Typography>
                  )}
                </Box>
                <ButtonBase
                  onClick={() => onRemove(m.in_rxcui)}
                  aria-label={`Remove ${m.display_name}`}
                  sx={{
                    flex: 'none',
                    minHeight: { xs: r(44), sm: r(36) },
                    px: r(12),
                    borderRadius: radius.sm,
                    color: color.inkSoft,
                    fontSize: r(13),
                    fontWeight: 600,
                    '&:hover': { color: color.ink, backgroundColor: color.lineSoft },
                  }}
                >
                  Remove
                </ButtonBase>
              </Box>
            ))}
          </Box>
        </>
      )}

      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: r(10),
          flex: 'none',
          borderTop: `${r(1)} solid ${color.lineSoft}`,
          pt: r(8),
          px: r(16),
          pb: r(8),
        }}
      >
        {/* One line, never wrapping at the panel's width (Aravind, 2026-10-09,
            round 5). */}
        <Typography
          component="p"
          sx={{
            m: 0,
            flex: 1,
            minWidth: 0,
            fontSize: r(11),
            color: color.inkSoft,
            lineHeight: 1.3,
            display: 'flex',
            alignItems: 'center',
            gap: r(4),
            whiteSpace: 'nowrap',
          }}
        >
          <LockOutlinedIcon sx={{ fontSize: r(12), flex: 'none' }} aria-hidden />
          <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
            Cleared when you close this tab.
          </Box>
        </Typography>
        <ButtonBase
          onClick={onClose}
          sx={{
            flex: 'none',
            minHeight: { xs: r(44), sm: r(36) },
            px: r(16),
            borderRadius: r(10),
            backgroundColor: color.accent,
            color: color.accentInk,
            fontWeight: 700,
            fontSize: r(14),
            '&:hover': { backgroundColor: color.accentHover },
          }}
        >
          Done
        </ButtonBase>
      </Box>
    </Box>
  );
}
